import * as THREE from 'three';
import { PRIORITY } from '../core/Engine.js';
import { Materials } from '../render/Materials.js';
import { expDamp, clamp } from '../util/math.js';
import {
  BONE, BONE_COUNT, createBones, applyBindPose, buildSoldierAssets, ikHandsToWeapon,
} from '../ai/SoldierRig.js';

/**
 * =============================================================================
 * LOBBY — a tela que aparece quando alguém abre o endereço limpo
 * =============================================================================
 *
 * POR QUE ELE EXISTE
 *
 * Até aqui, abrir o site construía a partida inteira antes de perguntar
 * qualquer coisa: 491 colisores, navgrid, 95 texturas procedurais, IA, rede,
 * pós-processamento e cadeia de FX. Medido em `tools/measure-ram.mjs` no estado
 * anterior: 37,8 MB de heap, 95 texturas, 90 geometrias, 226 draw calls e
 * 165.749 triângulos — tudo isso para uma pessoa que ainda estava escolhendo o
 * nome. Em máquina apertada isso é o suficiente para engasgar ou derrubar a aba.
 *
 * O lobby inverte a ordem: primeiro a tela, e a partida só é construída quando
 * alguém decide entrar. Quem nunca clica em "entrar" nunca paga pelo mapa.
 *
 * COMO A TROCA DE MODO ACONTECE
 *
 * `src/main.js` decide, na primeira linha, entre montar o LOBBY ou a PARTIDA, e
 * cada modo registra um conjunto de sistemas diferente. Entrar na partida é uma
 * RECARGA de página com `?play=1`, não uma transição viva — a mesma decisão que
 * a troca de mapa já tinha tomado, e pelo mesmo motivo: metade dos sistemas
 * guarda referência ao que o `Level` criou no init, e desmontar isso vivo seria
 * uma segunda máquina de estados para um caso que só acontece entre partidas.
 * A recarga também é o que devolve a memória ao sistema operacional de verdade.
 *
 * O QUE A CENA TEM
 *
 * Um soldado — o MESMO rig skinado dos inimigos e dos avatares remotos
 * (`src/ai/SoldierRig.js`), com a carabina já nas duas mãos por IK — parado num
 * estrado, respirando, trocando o peso de perna e olhando em volta de vez em
 * quando. Atrás dele, um pedaço de hangar sugerido em meia dúzia de volumes.
 * Luz de três pontos: chave quente à frente, recorte frio atrás (é ele que
 * separa a silhueta do fundo escuro) e preenchimento hemisférico fraco.
 *
 * O soldado é enquadrado à DIREITA do quadro de propósito: a metade esquerda
 * fica livre para o menu se espalhar, que é como todo jogo do gênero monta esta
 * tela hoje. `setEnquadramento()` move essa marca se o menu mudar de lado.
 */

/** Onde o soldado cai no eixo horizontal da tela. 0 = esquerda, 1 = direita. */
const ENQUADRAMENTO = 0.70;

/** Superfícies em 256 px: a 4 m de distância ninguém lê 512, e custa 4x menos. */
const TEX = { size: 256 };

export class LobbySystem {
  constructor() {
    this.name = 'lobby';
    this.priority = PRIORITY.WORLD;
    this.pronto = false;
    this.enquadramento = ENQUADRAMENTO;

    this._t = 0;
    this._olharAte = 0;
    this._olharYaw = 0;
    this._olharPitch = 0;
    this._yawAtual = 0;
    this._pitchAtual = 0;
    this._paralaxeX = 0;
    this._paralaxeY = 0;
    this._alvoX = 0;
    this._alvoY = 0;
  }

  init(engine) {
    this.engine = engine;
    engine.provide('lobby', this);
    engine.lobby = this;
    try {
      this._montar(engine);
      this.pronto = true;
    } catch (err) {
      // Um lobby quebrado não pode impedir a pessoa de chegar ao menu e clicar
      // em entrar: sem cena, sobra o fundo escuro e a interface por cima.
      console.error('[lobby] falhou ao montar a cena, seguindo só com o menu', err);
    }
    this._ouvirMouse();
  }

  // ---------------------------------------------------------------------------
  // Cena
  // ---------------------------------------------------------------------------

  _montar(engine) {
    const scene = engine.scene;
    this.group = new THREE.Group();
    this.group.name = 'lobby';
    scene.add(this.group);

    scene.background = new THREE.Color(0x06080a);
    scene.fog = new THREE.FogExp2(0x06080a, 0.085);

    // Duas correções que a primeira captura obrigou:
    //
    // 1. REPEAT. O `repeat` do kit é medido contra a UV da malha, não contra
    //    metros. Um plano de 46 m com repeat 6 estica um tile por 7,7 m e o
    //    concreto vira mármore — foi exatamente o que apareceu no chão.
    // 2. COR. As superfícies do jogo foram calibradas para sol de deserto. Sob
    //    a luz de estúdio deste lobby elas leem claras demais e o boneco some
    //    dentro do fundo. `color` multiplica o albedo, então escurecer aqui é
    //    de graça e mantém o relevo do normal map.
    const sup = (nome, repeat, cor, extra) => Materials.get(nome, {
      ...TEX, repeat, clone: true, overrides: { color: new THREE.Color(cor), ...extra },
    });

    // O chão leva `normalScale` baixo além da cor: a 16 repetições o relevo do
    // concreto vira ruído de alta frequência e o piso lê como cascalho molhado
    // — foi o que a segunda captura mostrou, um chão claro brigando com uma
    // cena escura. Aqui ele é quase preto e quase liso, e quem desenha o piso é
    // o foco de luz, não a textura.
    // O estrado saiu do `gunMetal`: metal com roughness 1 e um PMREM de 64x32
    // reflete quase nada, então ele ficava mais escuro que o chão e o boneco
    // parecia flutuar sobre um buraco. Concreto claro é dielétrico, acende com
    // as luzes que já existem e devolve a base de apoio ao boneco.
    this._materiais = {
      // `envMapIntensity` baixo no chão, e é o ajuste que mais mudou a tela.
      // Escurecer o albedo não bastava: um plano grande visto quase de lado
      // devolve o ambiente por Fresnel, e esse termo NÃO é multiplicado pela
      // cor — daí o piso continuar cinza-azulado com albedo de 2%. Pintar o
      // chão de vermelho puro foi o que separou as duas causas: a cor mudava,
      // o brilho rasante não.
      chao: sup('concrete', 16, 0x050607, {
        normalScale: new THREE.Vector2(0.3, 0.3), envMapIntensity: 0.12,
      }),
      parede: sup('metalPainted', 9, 0x1f2328),
      estrado: sup('concrete', 5, 0x6e747a),
      caixa: sup('woodPlank', 2.4, 0x4a3826),
      tambor: sup('rust', 2.2, 0x4e3a2b),
    };

    this._ambiente(engine);
    this._cenario();
    this._luzes(engine);
    this._soldado(engine);
    this._camera(engine);
  }

  /**
   * Mapa de ambiente do lobby — o passo sem o qual esta tela não funciona.
   *
   * Os materiais do jogo saem de `Materials.get` com `metalness: 1` (o escalar
   * multiplica o mapa empacotado). Metal sem nada para refletir é PRETO, e foi
   * literalmente o que a captura mostrou: o estrado de aço e a parede pintada
   * chapados em preto, enquanto o chão — o único dielétrico da cena — era a
   * única coisa acesa. Parecia um chão de neve num quarto vazio.
   *
   * O jogo resolve isso no `Lighting.js`, com um PMREM feito a partir do domo de
   * céu; aqui não há céu nenhum. Então o lobby cozinha o seu próprio ambiente a
   * partir de um equirretangular de 64x32 gerado em código: gradiente de teto
   * frio para piso quente, mais um borrão claro do lado da luz-chave. São 8 KB
   * de textura de origem e um cubemap pequeno — o suficiente para o metal ter o
   * que refletir, que é tudo o que se pede dele numa cena fechada.
   */
  _ambiente(engine) {
    const W = 64, H = 32;
    const dados = new Uint8Array(W * H * 4);
    const teto = new THREE.Color(0x2b3a4e);
    const piso = new THREE.Color(0x120e0a);
    const brilho = new THREE.Color(0xffd9ab);
    const c = new THREE.Color();

    for (let y = 0; y < H; y++) {
      const v = y / (H - 1);                       // 0 = topo
      for (let x = 0; x < W; x++) {
        const u = x / (W - 1);
        c.copy(teto).lerp(piso, v * v);
        // Borrão da chave: mesma direção da direcional quente, para o reflexo
        // não contar uma história diferente da luz.
        const d = Math.hypot(Math.min(Math.abs(u - 0.12), 1 - Math.abs(u - 0.12)) * 2.2, (v - 0.30) * 1.6);
        const k = Math.max(0, 1 - d * 2.4);
        if (k > 0) c.lerp(brilho, k * k * 0.85);
        const i = (y * W + x) * 4;
        dados[i] = Math.min(255, c.r * 255);
        dados[i + 1] = Math.min(255, c.g * 255);
        dados[i + 2] = Math.min(255, c.b * 255);
        dados[i + 3] = 255;
      }
    }

    const eq = new THREE.DataTexture(dados, W, H, THREE.RGBAFormat);
    eq.mapping = THREE.EquirectangularReflectionMapping;
    eq.colorSpace = THREE.SRGBColorSpace;
    eq.needsUpdate = true;

    try {
      const pmrem = new THREE.PMREMGenerator(engine.renderer);
      this._envRT = pmrem.fromEquirectangular(eq);
      engine.scene.environment = this._envRT.texture;
      // Reflexo de ambiente é preenchimento, não fonte: acima de ~0,7 ele
      // achata o modelado que as três luzes acabaram de construir.
      engine.scene.environmentIntensity = 0.65;
      pmrem.dispose();
    } catch (err) {
      console.warn('[lobby] sem mapa de ambiente; metais vão ficar escuros', err);
    }
    eq.dispose();
  }

  /**
   * O fundo. Meia dúzia de caixas: o que importa aqui é a SILHUETA por trás do
   * soldado e ter superfície onde o recorte frio possa bater — um fundo
   * literalmente vazio faz o boneco parecer recortado e colado.
   */
  _cenario() {
    const cx = new THREE.BoxGeometry(1, 1, 1);
    const add = (mat, x, y, z, sx, sy, sz, ry = 0) => {
      const m = new THREE.Mesh(cx, mat);
      m.position.set(x, y, z);
      m.scale.set(sx, sy, sz);
      m.rotation.y = ry;
      m.castShadow = true;
      m.receiveShadow = true;
      this.group.add(m);
      return m;
    };

    // Chão: plano grande, uma malha, recebendo sombra. A névoa exponencial come
    // a borda antes de ela aparecer, então não precisa de muro em volta.
    const chao = new THREE.Mesh(new THREE.PlaneGeometry(46, 46), this._materiais.chao);
    chao.rotation.x = -Math.PI / 2;
    chao.receiveShadow = true;
    this.group.add(chao);

    // Estrado sob os pés: dá base ao boneco e uma aresta clara embaixo dele.
    const estrado = new THREE.Mesh(new THREE.CylinderGeometry(1.15, 1.22, 0.10, 24), this._materiais.estrado);
    estrado.position.y = 0.05;
    estrado.receiveShadow = true;
    estrado.castShadow = true;
    this.group.add(estrado);

    // Parede de fundo, recuada, com dois vãos escuros que leem como portão.
    add(this._materiais.parede, 0, 2.6, -5.2, 15, 5.2, 0.35);
    add(this._materiais.parede, -3.4, 1.35, -4.95, 2.2, 2.7, 0.16);
    add(this._materiais.parede, 3.4, 1.35, -4.95, 2.2, 2.7, 0.16);

    // Pilares laterais: cortam o quadro e dão profundidade sem entrar na frente.
    add(this._materiais.parede, -4.6, 2.2, -2.4, 0.42, 4.4, 0.42);
    add(this._materiais.parede, 4.6, 2.2, -2.4, 0.42, 4.4, 0.42);

    // Adereço de canto, recuado: na primeira captura a pilha de caixotes estava
    // a 2 m da câmera e roubava o quadro do boneco. Adereço de fundo é para dar
    // escala e cortar a parede, não para ser o assunto.
    add(this._materiais.caixa, -3.15, 0.42, -3.5, 0.86, 0.84, 0.86, 0.18);
    add(this._materiais.caixa, -2.95, 1.20, -3.6, 0.70, 0.70, 0.70, -0.42);
    add(this._materiais.caixa, 3.05, 0.36, -3.7, 0.74, 0.72, 0.74, -0.26);

    const tambor = new THREE.Mesh(new THREE.CylinderGeometry(0.29, 0.29, 0.86, 14), this._materiais.tambor);
    tambor.position.set(2.45, 0.43, -3.0);
    tambor.castShadow = true;
    tambor.receiveShadow = true;
    this.group.add(tambor);

    // Rack de armas atrás, só silhueta.
    add(this._materiais.estrado, -3.6, 1.0, -4.4, 1.6, 0.08, 0.5);
    add(this._materiais.estrado, -3.6, 1.7, -4.4, 1.6, 0.08, 0.5);
    add(this._materiais.estrado, -4.35, 1.0, -4.4, 0.08, 2.0, 0.5);
    add(this._materiais.estrado, -2.85, 1.0, -4.4, 0.08, 2.0, 0.5);
  }

  /**
   * Três pontos, um só com sombra.
   *
   * A chave é quente e vem de cima à frente-esquerda (é ela que modela o rosto e
   * o colete). O RECORTE é o que faz esta tela funcionar: uma direcional fria,
   * forte, atrás e do lado oposto, desenhando uma linha de luz no ombro e no
   * capacete. Sem ela o boneco escuro sobre fundo escuro vira mancha.
   * O preenchimento hemisférico só levanta o preto o suficiente para o tecido
   * não fechar em zero.
   */
  _luzes(engine) {
    const chave = new THREE.DirectionalLight(0xffd2a1, 4.6);
    chave.position.set(2.6, 3.4, 3.1);
    chave.target.position.set(0, 1.05, 0);
    chave.castShadow = true;
    // Frustum apertado em volta de UM boneco: 1024 aqui é mais nítido que 2048
    // cobrindo um mapa inteiro, e custa um quarto da memória.
    chave.shadow.mapSize.set(1024, 1024);
    chave.shadow.camera.left = -3.5;
    chave.shadow.camera.right = 3.5;
    chave.shadow.camera.top = 3.5;
    chave.shadow.camera.bottom = -1;
    chave.shadow.camera.near = 0.5;
    chave.shadow.camera.far = 14;
    chave.shadow.bias = -0.0012;
    chave.shadow.normalBias = 0.02;
    this.group.add(chave, chave.target);
    this.chave = chave;

    const recorte = new THREE.DirectionalLight(0x8fb6ff, 5.5);
    recorte.position.set(-3.4, 2.5, -3.0);
    recorte.target.position.set(0, 1.2, 0);
    this.group.add(recorte, recorte.target);

    // Preenchimento fraco de propósito: é ele que decide se a tela tem contraste
    // ou se vira aquela iluminação de estúdio chapada em que nada tem sombra.
    const preench = new THREE.HemisphereLight(0x243448, 0x0c0a08, 0.45);
    this.group.add(preench);

    // Foco de cima, fechado no estrado. É ele que desenha a POÇA DE LUZ em
    // volta dos pés: sem isso o chão precisa de textura clara para não sumir, e
    // um chão claro brigando com uma cena escura foi o defeito da captura
    // anterior. Com o foco, o piso pode ser quase preto e ainda assim ter forma.
    const foco = new THREE.SpotLight(0xfff0d8, 90, 9, 0.62, 0.85, 1.6);
    foco.position.set(0.4, 5.2, 1.1);
    foco.target.position.set(0, 0, 0);
    this.group.add(foco, foco.target);
    this.foco = foco;

    // Prática: lâmpada âmbar baixa, do lado do rack. Serve de motivação para a
    // chave e põe um ponto quente no canto escuro do quadro.
    const pratica = new THREE.PointLight(0xffb35c, 6, 7, 2);
    pratica.position.set(-3.2, 2.15, -3.6);
    this.group.add(pratica);
    this.pratica = pratica;

    void engine;
  }

  /**
   * O boneco. É literalmente o mesmo rig do inimigo e do jogador remoto —
   * `buildSoldierAssets` guarda um único conjunto de geometria/material em
   * módulo, então isto custa uma SkinnedMesh e 20 ossos, mais nada.
   */
  _soldado(engine) {
    const assets = buildSoldierAssets(engine.quality || 'high');
    this.assets = assets;

    this.bones = createBones();
    applyBindPose(this.bones);
    this.skeleton = new THREE.Skeleton(this.bones, assets.boneInverses);

    // Variante 0 é a areia/tan; é a que lê melhor sob a chave quente.
    this.mesh = new THREE.SkinnedMesh(assets.variants[0], assets.materials);
    this.mesh.castShadow = true;
    this.mesh.receiveShadow = true;
    this.mesh.frustumCulled = false;
    this.mesh.add(this.bones[0]);
    this.mesh.bind(this.skeleton);

    this.boneco = new THREE.Group();
    this.boneco.name = 'lobby:soldado';
    // O rig olha para -Z com yaw 0; a câmera está em +Z, então ele gira para
    // encarar quem está do outro lado da tela, com um oitavo de volta a menos
    // para não ficar de frente chapada (três quartos lê melhor que retrato).
    this.boneco.rotation.y = Math.PI - 0.42;
    this.boneco.position.set(0, 0.10, 0);
    this.boneco.add(this.mesh);
    this.group.add(this.boneco);

    // Pose de descanso guardada: a animação de ócio soma em cima disto.
    this._pose0 = this.bones.map((b) => ({
      px: b.position.x, py: b.position.y, pz: b.position.z,
      q: b.quaternion.clone(),
    }));
  }

  _camera(engine) {
    const cam = engine.camera;
    cam.fov = 30;                 // lente longa: comprime o rosto e some com a distorção de borda
    cam.near = 0.05;
    cam.far = 60;
    cam.updateProjectionMatrix();
    // A 3,3 m com 34° o boneco chegava cortado nos pés e a pilha de caixotes
    // enchia meia tela. 4,9 m com 30° pega o corpo inteiro com folga em cima,
    // que é o enquadramento que este tipo de tela usa.
    this._camBase = new THREE.Vector3(0.34, 1.34, 4.90);
    this._alvoBase = new THREE.Vector3(0, 1.00, 0);
    this._aplicarCamera(0);
  }

  /**
   * Enquadramento horizontal sem mexer na posição do boneco.
   *
   * `setViewOffset` desloca o frustum, então o soldado vai para a direita da
   * tela mantendo a lente e a perspectiva exatas — girar a câmera para o lado
   * faria o mesmo serviço torcendo a geometria dele. Aqui a metade esquerda
   * fica vazia para o menu, e o boneco continua sendo visto de frente.
   */
  _aplicarCamera(dt) {
    const cam = this.engine.camera;
    const w = this.engine.renderer.domElement.clientWidth || window.innerWidth;
    const h = this.engine.renderer.domElement.clientHeight || window.innerHeight;

    // Em tela estreita o menu ocupa tudo e não sobra metade nenhuma: aí o
    // enquadramento volta ao centro, senão o boneco sai pela borda.
    const estreito = w < 900;
    const frac = estreito ? 0.5 : this.enquadramento;
    const desloc = (frac - 0.5) * w;
    if (Math.abs(desloc) > 0.5) cam.setViewOffset(w, h, -desloc, 0, w, h);
    else cam.clearViewOffset();

    // Deriva lenta + paralaxe do mouse. Amplitudes pequenas de propósito: a
    // tela tem que respirar, não balançar.
    this._paralaxeX = expDamp(this._paralaxeX, this._alvoX, 3.5, dt || 0.016);
    this._paralaxeY = expDamp(this._paralaxeY, this._alvoY, 3.5, dt || 0.016);
    const t = this._t;
    const dx = Math.sin(t * 0.17) * 0.075 + this._paralaxeX * 0.16;
    const dy = Math.sin(t * 0.23 + 1.3) * 0.035 + this._paralaxeY * 0.09;

    cam.position.set(this._camBase.x + dx, this._camBase.y + dy, this._camBase.z);
    cam.lookAt(this._alvoBase.x + dx * 0.35, this._alvoBase.y + dy * 0.4, this._alvoBase.z);
  }

  _ouvirMouse() {
    this._onMove = (e) => {
      const w = window.innerWidth || 1, h = window.innerHeight || 1;
      this._alvoX = clamp((e.clientX / w) * 2 - 1, -1, 1);
      this._alvoY = clamp((e.clientY / h) * 2 - 1, -1, 1);
    };
    window.addEventListener('pointermove', this._onMove, { passive: true });
  }

  // ---------------------------------------------------------------------------
  // Ócio
  // ---------------------------------------------------------------------------

  update(dt, alpha, engine) {
    if (!this.pronto) return;
    try { this._animar(dt); } catch { /* animação não derruba a tela */ }
    this._aplicarCamera(dt);
    void alpha; void engine;
  }

  /**
   * Respiração, troca de peso e olhadas.
   *
   * Tudo é somado sobre a pose de descanso, e as duas mãos são re-soldadas na
   * arma DEPOIS — a IK é o que impede o braço de descolar do guarda-mão quando
   * o tronco se mexe. É a mesma função que a IA usa; o custo é dois solves de
   * dois ossos por quadro.
   */
  _animar(dt) {
    this._t += dt;
    const t = this._t;
    const b = this.bones;
    const p0 = this._pose0;

    const resp = Math.sin(t * 1.05);              // ~5,7 respirações por minuto, em repouso
    const peso = Math.sin(t * 0.21);              // troca de perna de apoio, ciclo de ~30 s
    const micro = Math.sin(t * 2.7) * 0.0015;     // tremor que impede a pose de ler como estátua

    const hips = b[BONE.hips];
    hips.position.set(
      p0[BONE.hips].px + peso * 0.028,
      p0[BONE.hips].py + resp * 0.005 - Math.abs(peso) * 0.006 + micro,
      p0[BONE.hips].pz,
    );
    hips.rotation.set(0, peso * 0.045, -peso * 0.035);

    b[BONE.spine].rotation.set(-0.015 + resp * 0.014, peso * 0.03, 0);
    b[BONE.chest].rotation.set(resp * 0.011, peso * 0.035, peso * 0.012);

    // Olhadas: a cada 3–7 s escolhe um alvo novo e vai até ele amortecido. O
    // pescoço leva um terço do giro, a cabeça o resto — girar só a cabeça dá
    // aquele efeito de coruja que denuncia boneco.
    if (t > this._olharAte) {
      this._olharAte = t + 3 + Math.random() * 4;
      this._olharYaw = (Math.random() * 2 - 1) * 0.42;
      this._olharPitch = (Math.random() * 2 - 1) * 0.14;
    }
    this._yawAtual = expDamp(this._yawAtual, this._olharYaw, 2.2, dt);
    this._pitchAtual = expDamp(this._pitchAtual, this._olharPitch, 2.2, dt);
    b[BONE.neck].rotation.set(this._pitchAtual * 0.35, this._yawAtual * 0.32, 0);
    b[BONE.head].rotation.set(this._pitchAtual * 0.65, this._yawAtual * 0.68, this._yawAtual * -0.08);

    // A arma acompanha o tronco com atraso, e sobe/desce com a respiração.
    const w = b[BONE.weapon];
    w.position.set(
      p0[BONE.weapon].px + peso * 0.006,
      p0[BONE.weapon].py + resp * 0.004,
      p0[BONE.weapon].pz,
    );
    w.rotation.set(resp * 0.012, peso * 0.02, peso * 0.03);

    hips.updateMatrixWorld(true);
    ikHandsToWeapon(b);
    hips.updateMatrixWorld(true);
  }

  resize() { if (this.pronto) this._aplicarCamera(0); }

  /** Move a marca de onde o boneco fica no eixo X da tela (0 esq., 1 dir.). */
  setEnquadramento(f) {
    this.enquadramento = clamp(Number(f) || 0.5, 0.2, 0.85);
    if (this.pronto) this._aplicarCamera(0);
  }

  // ---------------------------------------------------------------------------
  // Sair do lobby
  // ---------------------------------------------------------------------------

  /**
   * Entra na partida. É uma RECARGA com `?play=1`, e é de propósito:
   *
   *  - é a recarga que devolve ao sistema as texturas, as geometrias e o
   *    contexto WebGL deste lobby — descartar isso à mão deixa lixo em GPU que
   *    nenhum `dispose()` alcança de forma confiável;
   *  - o `Level.init` é síncrono e roda no boot, e metade dos sistemas guarda
   *    referência ao que ele criou. Construir a partida por cima de um motor já
   *    rodando seria uma segunda máquina de estados, exatamente o tipo de código
   *    que quebra em produção meses depois.
   *
   * Do lado de quem joga, a diferença é um piscar — e o mapa carrega uma vez só,
   * em vez de carregar antes de a pessoa ter escolhido qualquer coisa.
   */
  entrarNaPartida({ nome, sala, mapa } = {}) {
    const q = new URLSearchParams();
    q.set('play', '1');
    if (mapa) q.set('map', mapa);
    if (nome) q.set('name', nome);
    if (sala) q.set('room', sala);

    // Parâmetros de diagnóstico que já estavam na URL seguem para a partida:
    // quem está depurando com ?mp=0 não quer perder isso ao clicar em entrar.
    try {
      const atual = new URLSearchParams(location.search);
      for (const k of ['mp', 'server', 'voice', 'quality']) {
        const v = atual.get(k);
        if (v !== null && !q.has(k)) q.set(k, v);
      }
    } catch { /* fora do navegador */ }

    location.href = `${location.pathname}?${q.toString()}`;
  }

  dispose() {
    try {
      window.removeEventListener('pointermove', this._onMove);
      this.group?.removeFromParent();
      this.mesh?.geometry?.dispose?.();
      this.skeleton?.dispose?.();
    } catch { /* saída é melhor esforço */ }
  }
}
